import React, { useEffect, useState, useMemo } from 'react';
import { Card, Collapse } from 'reactstrap';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import CheckboxMarked from 'mdi-react/CheckboxMarkedIcon';
import CheckboxBlank from 'mdi-react/CheckboxBlankIcon';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import AppointmentTypeItem from './components/AppointmentTypeItem';
import { useGetTopics } from './hooks/useGetTopics';
import { useCreateTopic } from './hooks/useCreateTopic';
import { useUpdateTopics } from './hooks/useUpdateTopics';
import { useUpdateSingleTopic } from './hooks/useUpdateSingleTopic';
import { useDeleteTopic } from './hooks/useDeleteTopic';
import { useGetFacilityServices } from './hooks/useGetFacilityServices';
import ConfirmModal from './components/ConfirmModal';
import { toast } from 'react-toastify';

const SERVICE_CONFIG = [
  { key: 'veterinary', flag: 'veterinaryOffered', label: 'Veterinary Topics' },
  { key: 'boarding', flag: 'boardingOffered', label: 'Boarding Topics' },
  { key: 'grooming', flag: 'groomingOffered', label: 'Grooming Topics' },
];

export default function TopicsList() {
  const { data: allTopics, refetch } = useGetTopics();
  const { data: services } = useGetFacilityServices();
  const { mutateAsync: createTopic } = useCreateTopic();
  const { mutateAsync: updateTopics } = useUpdateTopics();
  const { mutateAsync: updateSingleTopic } = useUpdateSingleTopic();
  const { mutateAsync: deleteTopic } = useDeleteTopic();

  const [isTopicsOpened, setIsTopicsOpened] = useState(false);
  const [topics, setTopics] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [newTypeName, setNewTypeName] = useState('');
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    onConfirm: null,
    message: '',
  });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    isOpen: false,
    onConfirm: null,
    message: '',
  });

  useEffect(() => {
    if (allTopics) {
      setTopics(allTopics.map((v) => v));
    }
  }, [allTopics]);

  const enabledServices = useMemo(() => {
    console.log('[TopicsList] services data:', services);
    if (!services) return [];
    const enabled = SERVICE_CONFIG.filter((s) => services[s.flag]);
    console.log('[TopicsList] enabledServices:', enabled);
    return enabled;
  }, [services]);

  const topicsByService = useMemo(() => {
    const grouped = {};
    for (const svc of enabledServices) {
      grouped[svc.key] = topics.filter((t) => t.service === svc.key);
    }
    console.log('[TopicsList] topicsByService:', grouped);
    return grouped;
  }, [topics, enabledServices]);

  console.log('[TopicsList] allTopics:', allTopics, 'topics state:', topics, 'enabledServices count:', enabledServices.length);

  const handleTopicsCollapse = () => {
    setIsTopicsOpened((prev) => !prev);
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;

    const service = result.source.droppableId;
    const serviceTopics = topicsByService[service];
    const reordered = Array.from(serviceTopics);
    const [movedItem] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, movedItem);

    const updatedTopics = topics.map((t) => {
      if (t.service !== service) return t;
      const idx = reordered.findIndex((r) => r._id === t._id);
      return idx >= 0 ? { ...t, order: idx } : t;
    });
    setTopics(updatedTopics);

    try {
      await updateTopics(
        reordered.map((item, i) => ({
          ...item,
          order: i,
        })),
      );
      refetch();
    } catch (e) {
      setTopics(topics);
    }
  };

  const toggleCheck = async (id) => {
    const item = topics.find(({ _id }) => _id === id);

    if (item.isNew) {
      toast('Please, complete editing first', {
        type: 'info',
        style: { width: 'auto' },
      });
      return;
    }

    if (item.editable && !item.checked) {
      return setConfirmModal({
        isOpen: true,
        message:
          'Once this Topic is checked, it will not be able to be edited and cannot be deleted. This ensures data referencing it will always be able to be filtered by this name. Do you want to continue?',
        onConfirm: async () => {
          setConfirmModal({ ...confirmModal, isOpen: false });
          await updateSingleTopic({
            ...item,
            checked: true,
          });
          refetch();
        },
      });
    }

    await updateSingleTopic({
      ...item,
      checked: !item.checked,
    });
    refetch();
  };

  const handleAddCustomType = (service) => {
    const hasUnfinishedDraft = topics.some(
      (t) => t.isNew || !t._id || !t.title?.trim(),
    );

    if (hasUnfinishedDraft) {
      toast(
        'You already have an unsaved Topic. Please complete it before adding another.',
        { type: 'info', style: { width: 'auto' } },
      );
      return;
    }

    const serviceTopics = topicsByService[service] || [];
    const tempId = `temp-${Date.now()}`;

    const draft = {
      _id: tempId,
      title: '',
      checked: false,
      editable: true,
      isCustom: true,
      service,
      order: serviceTopics.length,
      isNew: true,
    };

    setTopics((prev) => [...prev, draft]);
    setEditingId(tempId);
    setNewTypeName('');
  };

  const handleDelete = async (_id) => {
    const item = topics.find((t) => t._id === _id);

    if (item?.isNew) {
      setTopics((prev) => prev.filter((t) => t._id !== _id));
      setEditingId(null);
      setNewTypeName('');
      return;
    }

    setDeleteConfirmModal({
      isOpen: true,
      message: `Are you sure you want to delete the Topic "${item.title}"?`,
      onConfirm: async () => {
        setDeleteConfirmModal((prev) => ({ ...prev, isOpen: false }));
        await deleteTopic({ _id });
        refetch();
      },
    });
  };

  const handleEdit = (id) => {
    const item = topics.find((t) => t._id === id);
    setNewTypeName(item.title || '');
    setEditingId(id);
  };

  const handleEditChange = (e) => {
    setNewTypeName(e.target.value);
  };

  const handleEditSave = async (id) => {
    const item = topics.find(({ _id }) => _id === id);
    const updatedTitle = newTypeName.trim();

    if (!updatedTitle) {
      return;
    }

    if (item.isNew) {
      await createTopic({
        title: updatedTitle,
        service: item.service,
        checked: false,
        isCustom: true,
      });
    } else {
      await updateSingleTopic({
        ...item,
        title: updatedTitle,
      });
    }

    setEditingId(null);
    setNewTypeName('');
    refetch();
  };

  if (enabledServices.length === 0) {
    return null;
  }

  return (
    <>
      <div>
        <div
          className="appointment-types-header"
          onClick={handleTopicsCollapse}
        >
          <div className="checkbox-icon">
            {isTopicsOpened ? (
              <CheckboxMarked color="#E3E6EB" />
            ) : (
              <CheckboxBlank color="#E3E6EB" />
            )}
          </div>
          <div className="appointment-types-title">Topics</div>
          <button
            className={`customize-arrow ${
              isTopicsOpened ? 'rotate-up' : ''
            }`}
          >
            <ChevronRightIcon />
          </button>
        </div>
        <div className="customize-item">
          <div className="customize-content flex-grow-1">
            <div className="customize-description mb-3">
              Topics that are checked will appear as dropdown choices
              in the Topic field of the Contact Facility form in the mobile app.
            </div>
            <Collapse isOpen={isTopicsOpened}>
              <p className="drag-item-description">
                <em>
                  Drag items to change the order in which the choices for
                  Topics appear in dropdown menus.
                </em>
              </p>
              {enabledServices.map((svc) => {
                const serviceTopics = topicsByService[svc.key] || [];
                return (
                  <div key={svc.key} className="mb-3">
                    <h6 className="topic-service-title">{svc.label}</h6>
                    <Card className="d-flex border rounded p-2 mb-2">
                      <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId={svc.key}>
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                            >
                              {serviceTopics.map((topic, index) => (
                                <Draggable
                                  key={topic._id}
                                  draggableId={topic._id}
                                  index={index}
                                >
                                  {(provided) => (
                                    <AppointmentTypeItem
                                      type={topic}
                                      index={index}
                                      provided={provided}
                                      toggleCheck={toggleCheck}
                                      editingId={editingId}
                                      newTypeName={newTypeName}
                                      handleEditChange={handleEditChange}
                                      handleEdit={handleEdit}
                                      handleEditSave={handleEditSave}
                                      handleDelete={handleDelete}
                                      setNewTypeName={setNewTypeName}
                                    />
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </Card>
                    <button
                      className="btn loyal-btn-new-parent mt-1"
                      onClick={() => handleAddCustomType(svc.key)}
                    >
                      + Add Custom Topic
                    </button>
                  </div>
                );
              })}
            </Collapse>
          </div>
        </div>
        <ConfirmModal
          centered
          isOpen={confirmModal.isOpen}
          toggle={() => setConfirmModal({ ...confirmModal, isOpen: false })}
          onConfirm={confirmModal.onConfirm}
          message={confirmModal.message}
        />
        <ConfirmModal
          isOpen={deleteConfirmModal.isOpen}
          toggle={() =>
            setDeleteConfirmModal({ ...deleteConfirmModal, isOpen: false })
          }
          onConfirm={deleteConfirmModal.onConfirm}
          message={deleteConfirmModal.message}
          title="Delete Topic"
        />
      </div>
    </>
  );
}
